import moment from "moment";
import { ScheduleType } from "@/core/api/booking/BookingScheduleType";
import { BookingFormModel } from "@/views/bookingForm/BookingFormModel";
import { Chain } from '@/core/util/Chain';

export class BookingFormModelFactory {
  static makeForBookingCreation({
    bookingChannelId,
    businessAccountId,
    tip,
    businessAccountConfig,
    paymentProviderName,
  }) {
    return new BookingFormModel({
      bookingChannelId,
      businessAccountId,
      paymentProviderName,
      tip,
      businessAccountConfig,
      pickupTime: moment().format("YYYY-MM-DDTHH:mm:ss"),
      scheduleType: ScheduleType.ASAP,
    });
  }

  /**
   *
   * @param {BookingHistoryDetail} booking
   * @return {BookingFormModel}
   */
  static makeFromBookingHistoryDetail(booking) {
    const scheduleType = (booking.getSecondsUntilPickupTime() >= 5 * 60) ? ScheduleType.PREBOOKING : ScheduleType.ASAP; // If pickup time is more than 5 minutes in the future then consider it as a pre-booking.
    const pickupTime = (scheduleType === ScheduleType.PREBOOKING) ? booking.getFormattedLocalPickupTimeAtTimezone() : booking.getFormattedCurrentTimeAtTimezone();

    return new BookingFormModel({
      bookingChannelId: booking.bookingChannelId,
      paymentProviderName: booking.payment.paymentProviderName,
      businessAccountId: booking.corporationId,

      scheduleType,
      pickupTime,
      timezone: booking.timezone,

      tip: {
        base: 'percentage',
        value: 0,
      },
      origin: {
        id: booking.origin.dispatchAddressId,
        description: booking.origin.address,
        source: booking.getOriginAddressSource(),
        coordinates: {
          latitude: booking.origin.latitude,
          longitude: booking.origin.longitude,
        },
        locationTypes: booking.origin.locationTypes,
      },
      destination: {
        id: booking.destination.dispatchAddressId,
        description: booking.destination.address,
        source: booking.getDestinationAddressSource(),
        coordinates: {
          latitude: booking.destination.latitude,
          longitude: booking.destination.longitude,
        },
        locationTypes: booking.destination.locationTypes,
      },
      waypoints: booking.waypoints.map((w) => {
        return {
          id: w.dispatchAddressId, // can be undefined
          description: w.address,
          source: booking.getWaypointAddressSource(w),
          coordinates: {
            latitude: w.latitude,
            longitude: w.longitude,
          },
          locationTypes: w.locationTypes,
        };
      }),
      createBookingRecipient: {
        isAdhocGuest: false,
        employeeId: booking.isBookingForBusinessPassenger() ? booking.getBusinessPassengerId() : null,
        guestId: booking.isBookingForBusinessGuest() ? booking.getBusinessGuestId() : null,

        givenName: booking.userInfo.givenName,
        familyName: booking.userInfo.familyName,
        phoneNumber: booking.userInfo.phone,
        email: booking.userInfo.email,
      },
      airportDetails: booking.flightInfo.hasFlightInfo() ? {
        time: Chain.start(booking.flightInfo.arrivalTime).map((arrivalTime) => {
          if (!arrivalTime) {
            return undefined;
          }
          // arrivalTime is an ISO string, convert to a moment object with a time instant at the booking's timezone please
          return moment.tz(arrivalTime, booking.timezone);
        }).end(),
        flightNumber: booking.flightInfo.number,
        terminal: booking.flightInfo.terminal,
        passengers: booking.flightInfo.totalPassengers,
        smallBags: booking.flightInfo.totalSmallBags,
        largeBags: booking.flightInfo.totalLargeBags,
      } : undefined,
    });
  }
}
